// Extracted from CaseBuddy.vue in refactor pass 2.
// Starting transaction loading, case detail editing, workflow checks, workflow signals, and action dispatch methods.
// These are Options API method definitions and intentionally use `this`.

import {
  abbreviateCaseBuddyHash,
  analyzeEntryUtcDateCompletion,
  analyzeNumericMainPathLabels,
  analyzeStartingMainTransactions,
  buildAddressAttributionAnalysis,
  buildCaseAddressColorCoverageAnalysis,
  buildCaseAddressLegendAnalysis,
  buildCaseConclusionState,
  buildCaseDepositHotWalletReviewAnalysis,
  buildCaseDepositPatternReviewAnalysis,
  buildCaseDormantAddressDeliverablesState,
  buildCaseDuplicateHashLabelerAnalysis,
  buildCaseExchangeAddressTableState,
  buildCaseInconsistencyReviewAnalysis,
  buildCaseKnownEntityCasingReviewAnalysis,
  buildCaseNoteHashRewriteAnalysis,
  buildCaseReportBodyState,
  buildCaseSuspiciousAddressTableState,
  buildCaseWhatWentWhereState,
  buildCaseWwwUsdReferenceReviewState,
  buildMainRawDataCoverage,
  buildPathOperationAnalysis,
  buildSaplingPreparationAnalysis,
  CASE_COLUMN_HEADERS,
  CASE_DEFAULT_COLS,
  CASE_ADDENDUM_VARIANT_NARRATIVE,
  CASE_ADDENDUM_VARIANT_STRUCTURED,
  CASE_DELIVERABLE_GROUPS,
  CASE_WORKFLOW_GROUPS,
  CASE_MAIN_SHEET_NAME,
  CASE_RAW_SHEET_NAME,
  CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING,
  CASE_REPORT_BODY_VARIANT_STANDARD,
  createCaseSheetState,
  getCaseWorkflowStepHelp,
  createDefaultCaseSupportState,
  createDefaultCaseUiState,
  createEmptyCaseSummary,
  createWorkbookSheetId,
  isLikelyPastedHeaderCell,
  isLikelyStartingTransactionHash,
  normalizeCaseMatrix,
  normalizeCaseUiState,
  normalizeCell,
  normalizeComparisonKey,
  parseCaseEntries,
  parseCaseUtcDate,
  rowHasMeaningfulData,
  rowLooksLikeCaseBuddyHeader
} from '../CaseBuddyCore.js';

export const caseBuddyWorkflowMethods = {
    clearStartingMainTransactionsDraft() {

      this.startingMainTransactionsPaste = '';
      this.startingMainTransactionDraftRows = [];
    },

    parseStartingMainTransactionPaste() {
      const raw = String(this.startingMainTransactionsPaste || '');
      const rows = [];
      let counter = 0;

      const pushRow = (hash, toAddress, sourceLine) => {
        const cleanHash = normalizeCell(hash);
        const cleanToAddress = normalizeCell(toAddress);
        if (!cleanHash && !cleanToAddress) return;
        if (isLikelyPastedHeaderCell(cleanHash)) return;

        counter += 1;
        rows.push({
          id: `starting-main-${Date.now().toString(36)}-${counter}`,
          sourceLine,
          hash: cleanHash,
          toAddress: cleanToAddress,
          status: 'ready',
          statusLabel: 'Will add'
        });
      };

      raw.split(/\r?\n/).forEach((line, lineIndex) => {
        const trimmed = line.trim();
        if (!trimmed) return;

        if (line.includes('\t')) {
          const cells = line.split('\t').map(cell => normalizeCell(cell));
          const nonEmptyCells = cells.filter(Boolean);
          if (nonEmptyCells.length === 0) return;

          if (cells.length >= 4) {
            const canonicalHash = cells[1] || '';
            const canonicalToAddress = cells[3] || '';
            const looksLikeCanonicalHeader = isLikelyPastedHeaderCell(canonicalHash) ||
              (!canonicalHash && isLikelyPastedHeaderCell(canonicalToAddress));

            if ((canonicalHash || canonicalToAddress) && !looksLikeCanonicalHeader) {
              pushRow(canonicalHash, canonicalToAddress, lineIndex + 1);
            }
            return;
          }

          if (cells.length >= 2 && cells[0] && !isLikelyPastedHeaderCell(cells[0])) {
            pushRow(cells[0], cells[1] || '', lineIndex + 1);
            return;
          }

          return;
        }

        trimmed
          .split(/[,;\s]+/)
          .map(part => normalizeCell(part))
          .filter(Boolean)
          .forEach(part => pushRow(part, '', lineIndex + 1));
      });

      this.startingMainTransactionDraftRows = rows;
      this.refreshStartingMainTransactionStagedStatuses();
    },

    getExistingMainTransactionPairInfo() {
      const mainData = this.getMatrixForLogicalSheet('main');
      const pairs = new Set();
      const hashes = new Set();

      (Array.isArray(mainData) ? mainData : []).slice(1).forEach((row) => {
        const hash = normalizeComparisonKey(row?.[1]);
        if (!hash) return;
        const toAddress = normalizeComparisonKey(row?.[3]);
        hashes.add(hash);
        pairs.add(`${hash}||${toAddress}`);
      });

      return { pairs, hashes };
    },

    refreshStartingMainTransactionStagedStatuses() {
      const { pairs: existingPairs, hashes: existingHashes } = this.getExistingMainTransactionPairInfo();
      const seenDraftPairs = new Set();

      this.startingMainTransactionDraftRows = this.startingMainTransactionDraftRows.map((row) => {
        const hash = normalizeComparisonKey(row.hash);
        const toAddress = normalizeComparisonKey(row.toAddress);
        const key = `${hash}||${toAddress}`;
        let status = 'ready';
        let statusLabel = 'Will add';

        if (!hash) {
          status = 'needs-hash';
          statusLabel = 'Needs hash';
        } else if (!isLikelyStartingTransactionHash(row.hash)) {
          status = 'needs-hash';
          statusLabel = 'Check hash';
        } else if (existingPairs.has(key)) {
          status = 'duplicate-existing';
          statusLabel = 'Already in Main Data';
        } else if (seenDraftPairs.has(key)) {
          status = 'duplicate-draft';
          statusLabel = 'Duplicate in draft';
        } else if (existingHashes.has(hash)) {
          status = 'different-to';
          statusLabel = 'Same hash, different To';
        }

        if (hash && !existingPairs.has(key) && !seenDraftPairs.has(key)) {
          seenDraftPairs.add(key);
        }

        return {
          ...row,
          hash: normalizeCell(row.hash),
          toAddress: normalizeCell(row.toAddress),
          status,
          statusLabel
        };
      });
    },

    loadStartingMainTransactionsIntoMainSheet() {
      this.refreshStartingMainTransactionStagedStatuses();
      const rowsToAdd = this.startingMainTransactionDraftRows.filter(row => row.status === 'ready' || row.status === 'different-to');

      if (rowsToAdd.length === 0) {
        this.showCaseToast('No staged starting transactions are ready to add.');
        return;
      }

      this.ensureCaseWorkbookScaffold({ silent: true });
      const mainSheet = this.findLogicalSheetMeta('main');
      if (!mainSheet) {
        this.showCaseToast('Could not find the Main Data sheet.');
        return;
      }

      const state = this.getSheetStateById(mainSheet.id);
      const data = normalizeCaseMatrix(state.data);
      if (!this.headerRowLooksCanonical(data[0])) {
        CASE_COLUMN_HEADERS.forEach((header, index) => {
          data[0][index] = header;
        });
      }

      let lastNonEmptyRow = 0;
      data.forEach((row, index) => {
        if (index > 0 && rowHasMeaningfulData(row)) lastNonEmptyRow = index;
      });

      const startRow = Math.max(1, lastNonEmptyRow + 1);
      const neededRows = startRow + rowsToAdd.length;
      while (data.length < neededRows) {
        data.push(Array(CASE_DEFAULT_COLS).fill(''));
      }

      rowsToAdd.forEach((draft, rowOffset) => {
        const nextRow = Array(CASE_DEFAULT_COLS).fill('');
        nextRow[1] = normalizeCell(draft.hash);
        nextRow[3] = normalizeCell(draft.toAddress);
        data[startRow + rowOffset] = nextRow;
      });

      this.writeSheetStateById(mainSheet.id, {
        ...state,
        data,
        frozenTop: 1
      });

      this.markCaseDataMutated('starting_main_transactions_loaded', {
        rowsAdded: rowsToAdd.length,
        skippedRows: this.startingMainTransactionDraftRows.length - rowsToAdd.length
      });

      if (this.autoSaveEnabled) {
        this.saveCurrentProjectFile({ persistInput: true, persistOutput: false, markSavedAt: true });
      }

      this.showStartingMainTransactionsModal = false;
      this.refreshCaseSummary();
      this.activateLogicalSheet('main');
      this.showCaseToast(`Loaded ${rowsToAdd.length} starting main transaction row${rowsToAdd.length === 1 ? '' : 's'} into Main Data. Run checks when ready to refresh the workflow.`);
      if (this.caseSimpleLaunchReturnAfterStartingModal) {
        this.caseSimpleLaunchReturnAfterStartingModal = false;
        this.caseSimpleLaunchStatus = `Loaded ${rowsToAdd.length} starting transaction row${rowsToAdd.length === 1 ? '' : 's'}. Run Auto to build the case.`;
        this.$nextTick?.(() => this.restoreCaseSimpleLaunch?.());
      }
    },

    getStartingMainTransactionConfirmationState() {
      const mainData = this.getMatrixForLogicalSheet('main');
      const analysis = analyzeStartingMainTransactions(mainData);
      const pass = analysis.mainTransactionCount > 0 && analysis.trashRowCount === 0;
      const status = pass ? 'pass' : 'fail';
      const message = analysis.mainTransactionCount > 0
        ? analysis.trashRowCount === 0
          ? `${analysis.mainTransactionCount} starting main transaction${analysis.mainTransactionCount === 1 ? '' : 's'} detected (${analysis.mode}).`
          : `${analysis.mainTransactionCount} starting main transaction${analysis.mainTransactionCount === 1 ? '' : 's'} detected, but ${analysis.trashRowCount} row${analysis.trashRowCount === 1 ? '' : 's'} have Column D without Column B in the starting-data area.`
        : 'No starting main transactions detected. Add rows with transaction hashes in Column B, with optional To Addresses in Column D.';

      return {
        analysis,
        pass,
        status,
        message,
        count: analysis.mainTransactionCount,
        trashRowCount: analysis.trashRowCount,
        mode: analysis.mode
      };
    },

    confirmStartingMainTransactionsLoaded(options = {}) {
      const state = this.getStartingMainTransactionConfirmationState();
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        main_transactions: this.buildCheckResult('main_transactions', state.status, state.message, {
          mode: state.mode,
          mainTransactionCount: state.count,
          trashRowCount: state.trashRowCount
        })
      };
      this.persistCaseSupportState();
      this.refreshCaseSummary({ light: true });

      if (!options.silent) {
        this.showCaseToast(state.pass
          ? `Confirmed ${state.count} starting main transaction row${state.count === 1 ? '' : 's'} in Main Data.`
          : state.message);
      }

      return state.pass;
    },

    openCaseDetailsEditor() {
      this.caseDetailsDraft = {
        caseName: this.caseDetails.caseName || '',
        caseId: this.caseDetails.caseId || '',
        caseType: this.caseDetails.caseType || ''
      };
      this.showCaseDetailsEditor = true;
    },

    cancelCaseDetailsEditor() {
      this.showCaseDetailsEditor = false;
    },

    saveCaseDetailsEditor() {
      this.caseSupportState.caseDetails = {
        caseName: String(this.caseDetailsDraft.caseName || '').trim().slice(0, 120),
        caseId: String(this.caseDetailsDraft.caseId || '').trim().slice(0, 80),
        caseType: String(this.caseDetailsDraft.caseType || '').trim().slice(0, 80)
      };
      this.showCaseDetailsEditor = false;
      this.markCaseDataMutated('case_details_updated', { metadataOnly: true });

      if (this.autoSaveEnabled) {
        this.saveCurrentProjectFile({ persistInput: true, persistOutput: false, markSavedAt: true });
      }

      this.showCaseToast('Case details saved.');
    },

    markCaseDataMutated(source = 'case_data_changed', details = {}) {
      const now = new Date().toISOString();
      const currentRevision = Number(this.caseSupportState.caseDataRevision) || 0;

      this.caseSupportState.caseDataRevision = currentRevision + 1;
      this.caseSupportState.dirty = true;
      if (!this.caseSupportState.dirtySinceUtc) {
        this.caseSupportState.dirtySinceUtc = now;
      }
      this.caseSupportState.lastMutation = {
        source,
        details,
        utc: now,
        revision: this.caseSupportState.caseDataRevision
      };
      this.bumpCasePerformanceCacheVersion?.();
      this.clearCaseAnalysisCache?.({ all: true });

      const isDirectSpreadsheetEdit = source === 'spreadsheet_cell_edit' || !!details.deferCasePersistence;
      if (isDirectSpreadsheetEdit) {
        this.queueCaseSupportStatePersist(350);
        this.queueCaseSummaryRefresh(1100, { idle: true, timeout: 4500, light: true });
      } else {
        this.persistCaseSupportState();
        this.queueCaseSummaryRefresh();
      }

      if (this.autoSaveEnabled) {
        this.$nextTick(() => {
          if (this.caseSupportState.dirty) {
            this.saveCurrentProjectFile({ persistInput: true, persistOutput: false, markSavedAt: true });
          }
        });
      }
    },

    markCaseFormattingMutated(source = 'case_formatting_changed', details = {}) {
      const now = new Date().toISOString();
      const currentRevision = Number(this.caseSupportState.caseDataRevision) || 0;

      this.caseSupportState.dirty = true;
      if (!this.caseSupportState.dirtySinceUtc) {
        this.caseSupportState.dirtySinceUtc = now;
      }
      this.caseSupportState.lastMutation = {
        source,
        details: {
          ...(details || {}),
          formattingOnly: true
        },
        utc: now,
        revision: currentRevision
      };
      this.clearCasePerformanceCache?.('view');

      if (details.deferCasePersistence) {
        this.queueCaseSupportStatePersist(350);
        this.queueCaseSummaryRefresh(1100, { idle: true, timeout: 4500, light: true });
      } else {
        this.persistCaseSupportState();
        this.queueCaseSummaryRefresh();
      }

      if (this.autoSaveEnabled) {
        this.$nextTick(() => {
          if (this.caseSupportState.dirty) {
            this.saveCurrentProjectFile({ persistInput: true, persistOutput: false, markSavedAt: true });
          }
        });
      }
    },

    markCaseSaved() {
      const now = new Date().toISOString();
      this.caseSupportState.dirty = false;
      this.caseSupportState.dirtySinceUtc = '';
      this.caseSupportState.lastSavedRevision = Number(this.caseSupportState.caseDataRevision) || 0;
      this.caseSupportState.lastSavedUtc = now;
      this.persistCaseSupportState();
    },

    setOutputReviewState(nextState = {}, options = {}) {
      this.caseSupportState.outputReview = {
        ...(this.caseSupportState.outputReview || createDefaultCaseSupportState().outputReview),
        ...nextState
      };
      if (options.deferPersist) {
        this.queueCaseSupportStatePersist(300);
      } else {
        this.persistCaseSupportState();
      }
      if (options.deferSummary) {
        this.queueCaseSummaryRefresh(900, { idle: true, timeout: 3500, light: true });
      } else {
        this.queueCaseSummaryRefresh();
      }
    },

    markOutputBufferGenerated({ title = 'Pending output', detail = '', sourceActionId = '', sourceDeliverableId = '', target = '' } = {}) {
      this.setOutputReviewState({
        status: 'pending',
        title,
        detail,
        generatedAtUtc: new Date().toISOString(),
        sourceActionId,
        sourceDeliverableId,
        target,
        reviewedAtUtc: ''
      });
    },

    markOutputBufferMutated(source = 'output_edited') {
      this.setOutputReviewState({
        status: 'pending',
        title: 'Manually edited output',
        detail: `Output buffer changed by ${source}.`,
        generatedAtUtc: new Date().toISOString(),
        reviewedAtUtc: ''
      }, { deferPersist: true, deferSummary: true });
    },

    markOutputReviewed() {
      if (!this.outputHasData) {
        this.showCaseToast('The output buffer is empty.');
        return;
      }

      this.setOutputReviewState({
        status: 'reviewed',
        title: this.caseSupportState.outputReview?.title || 'Reviewed output',
        reviewedAtUtc: new Date().toISOString()
      });
      this.showCaseToast('Output marked reviewed.');
    },

    registerCaseMutationWatchers(hot, scope = 'input') {
      if (!hot || this.watchedInputHotInstances.includes(hot)) return;

      this.watchedInputHotInstances.push(hot);

      const markEdit = (sourceLabel, extra = {}) => {
        this.markCaseDataMutated(sourceLabel, {
          scope,
          activeSheetName: this.$refs.inputSpreadsheet?.activeSheetName || '',
          ...extra
        });
      };

      hot.addHook?.('afterChange', (changes, source) => {
        if (!changes || source === 'loadData' || source === 'case_restore' || source === 'case_scaffold') return;
        const meaningfulChanges = changes.filter((change) => {
          if (!Array.isArray(change) || change.length < 4) return true;
          return String(change[2] ?? '') !== String(change[3] ?? '');
        });
        if (meaningfulChanges.length === 0) return;
        markEdit('spreadsheet_cell_edit', {
          source,
          changeCount: meaningfulChanges.length,
          deferCasePersistence: true
        });
      });

      ['afterCreateRow', 'afterRemoveRow', 'afterCreateCol', 'afterRemoveCol', 'afterRowMove', 'afterColumnMove'].forEach((hookName) => {
        hot.addHook?.(hookName, (...args) => markEdit(`spreadsheet_${hookName}`, { args: args.slice(0, 3), deferCasePersistence: true }));
      });
    },

    registerOutputMutationWatchers(hot) {
      if (!hot || this.watchedOutputHotInstances.includes(hot)) return;
      this.watchedOutputHotInstances.push(hot);

      hot.addHook?.('afterChange', (changes, source) => {
        if (!changes || source === 'loadData' || source === 'case_output_load') return;
        this.markOutputBufferMutated(source || 'output_edit');
      });
    },

    normalizeSheetName(name) {
      return String(name || '').trim().toLowerCase().replace(/[\s_/-]+/g, '');
    },

    isLogicalSheetName(name, role) {
      const normalized = this.normalizeSheetName(name);
      if (role === 'main') {
        return ['maindata', 'main', 'inputdata', 'input', 'caseinput'].includes(normalized);
      }
      if (role === 'raw') {
        return ['rawdata', 'raw', 'sourcedata', 'source'].includes(normalized);
      }
      return false;
    },

    findLogicalSheetMeta(role) {
      const workbook = this.$refs.inputSpreadsheet;
      const sheets = Array.isArray(workbook?.sheets) ? workbook.sheets : [];
      return sheets.find(sheet => this.isLogicalSheetName(sheet.name, role)) || null;
    },

    getSheetStateById(sheetId) {
      const workbook = this.$refs.inputSpreadsheet;
      if (!workbook || !sheetId) return createCaseSheetState({ includeHeaders: true });

      const sheetRef = workbook.getSheetRef?.(sheetId);
      const state = sheetRef?.getSheetState?.() || workbook.pendingSheetStates?.[sheetId] || createCaseSheetState({ includeHeaders: true });
      const dataRef = state?.data || [];
      const cacheKey = [
        'sheet-state',
        sheetId,
        Number(this.caseSupportState?.caseDataRevision) || 0,
        Array.isArray(dataRef) ? dataRef.length : 0,
        Array.isArray(dataRef?.[0]) ? dataRef[0].length : 0
      ].join('|');
      const cache = this.getCasePerformanceCache?.();
      const store = cache?.misc;
      const cached = store?.get(cacheKey);
      const normalizedData = cached && cached.dataRef === dataRef
        ? cached.data
        : normalizeCaseMatrix(dataRef);
      if (!cached || cached.dataRef !== dataRef) {
        store?.set(cacheKey, { dataRef, data: normalizedData });
      }
      return {
        ...createCaseSheetState({ includeHeaders: true }),
        ...(state || {}),
        data: normalizedData
      };
    },

    writeSheetStateById(sheetId, nextState) {
      const workbook = this.$refs.inputSpreadsheet;
      if (!workbook || !sheetId) return false;
      this.clearCasePerformanceCache?.('misc');
      this.clearCasePerformanceCache?.('state');
      this.clearCasePerformanceCache?.('view');

      const normalizedState = {
        ...createCaseSheetState({ includeHeaders: true }),
        ...(nextState || {}),
        data: normalizeCaseMatrix(nextState?.data || [])
      };

      const sheetRef = workbook.getSheetRef?.(sheetId);
      if (sheetRef?.applySheetState) {
        sheetRef.applySheetState(normalizedState);
      } else if (sheetRef?.loadData) {
        sheetRef.loadData(normalizedState.data);
      }

      if (workbook.pendingSheetStates) {
        workbook.pendingSheetStates[sheetId] = normalizedState;
      }

      return true;
    },

    headerRowLooksCanonical(row = []) {
      return rowLooksLikeCaseBuddyHeader(row);
    },

    seedHeadersForSheetId(sheetId, { force = false } = {}) {
      const state = this.getSheetStateById(sheetId);
      const data = normalizeCaseMatrix(state.data);
      const headerRow = Array.isArray(data[0]) ? data[0] : [];
      const headerHasData = rowHasMeaningfulData(headerRow);

      if (headerHasData && this.headerRowLooksCanonical(headerRow) && !force) {
        return false;
      }

      if (headerHasData && !force) {
        return false;
      }

      CASE_COLUMN_HEADERS.forEach((header, index) => {
        data[0][index] = header;
      });

      this.writeSheetStateById(sheetId, {
        ...state,
        data,
        frozenTop: 1
      });
      return true;
    },

    seedCaseHeaders({ force = false } = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const mainSheet = this.findLogicalSheetMeta('main');
      const rawSheet = this.findLogicalSheetMeta('raw');
      let changed = false;

      [mainSheet, rawSheet].filter(Boolean).forEach((sheet) => {
        changed = this.seedHeadersForSheetId(sheet.id, { force }) || changed;
      });

      if (changed) {
        this.markCaseDataMutated('case_headers_seeded');
        if (this.autoSaveEnabled) this.saveSpreadsheetFromEvent();
        this.showCaseToast('Case Buddy headers seeded on Main Data and Raw Data.');
      } else {
        this.showCaseToast('Headers already appear to be present, or row 1 has existing data.');
      }
    },

    ensureCaseWorkbookScaffold({ silent = false } = {}) {
      const workbook = this.$refs.inputSpreadsheet;
      if (!workbook) return false;

      const sheets = Array.isArray(workbook.sheets) ? workbook.sheets : [];
      let changed = false;
      let mainSheet = this.findLogicalSheetMeta('main');
      let rawSheet = this.findLogicalSheetMeta('raw');

      if (!mainSheet) {
        const defaultSheet = sheets.find(sheet => this.normalizeSheetName(sheet.name) === 'sheet1') || sheets[0] || null;
        if (defaultSheet) {
          defaultSheet.name = CASE_MAIN_SHEET_NAME;
          mainSheet = defaultSheet;
        } else {
          mainSheet = workbook.createSheetMeta?.(CASE_MAIN_SHEET_NAME) || { id: createWorkbookSheetId(), name: CASE_MAIN_SHEET_NAME };
          sheets.push(mainSheet);
          workbook.ensureSelectionEntry?.(mainSheet.id);
          workbook.pendingSheetStates[mainSheet.id] = createCaseSheetState({ includeHeaders: true });
        }
        changed = true;
      }

      if (!rawSheet) {
        rawSheet = workbook.createSheetMeta?.(CASE_RAW_SHEET_NAME) || { id: createWorkbookSheetId(), name: CASE_RAW_SHEET_NAME };
        sheets.push(rawSheet);
        workbook.ensureSelectionEntry?.(rawSheet.id);
        workbook.pendingSheetStates[rawSheet.id] = createCaseSheetState({ includeHeaders: true });
        changed = true;
      }

      if (mainSheet) changed = this.seedHeadersForSheetId(mainSheet.id, { force: false }) || changed;
      if (rawSheet) changed = this.seedHeadersForSheetId(rawSheet.id, { force: false }) || changed;

      if (changed) {
        workbook.emitActiveSheetContext?.();
        this.markCaseDataMutated('case_workbook_scaffold_updated');
        if (this.autoSaveEnabled) {
          this.saveSpreadsheetFromEvent();
        }
        this.queueCaseSummaryRefresh();
        if (!silent) this.showCaseToast('Case workbook scaffold is ready: Main Data and Raw Data tabs are present.');
      } else if (!silent) {
        this.showCaseToast('Case workbook scaffold is already present.');
      }

      return changed;
    },

    activateLogicalSheet(role) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const sheet = this.findLogicalSheetMeta(role);
      if (!sheet) {
        this.showCaseToast(`Could not find the ${role} sheet.`);
        return false;
      }

      this.$refs.inputSpreadsheet?.activateSheet?.(sheet.id);
      this.scrollToSection('case-input');
      return true;
    },

    getMatrixForLogicalSheet(role) {
      const sheet = this.findLogicalSheetMeta(role);
      if (!sheet) return [];
      return this.getSheetStateById(sheet.id).data || [];
    },

    getOutputMatrix() {
      const state = this.$refs.outputSpreadsheet?.getSheetState?.();
      const dataRef = state?.data || this.$refs.outputSpreadsheet?.getData?.() || [];
      const cacheKey = [
        'output-matrix',
        Number(this.caseSupportState?.caseDataRevision) || 0,
        this.caseSupportState?.outputReview?.generatedAtUtc || '',
        Array.isArray(dataRef) ? dataRef.length : 0,
        Array.isArray(dataRef?.[0]) ? dataRef[0].length : 0
      ].join('|');
      const store = this.getCasePerformanceCache?.()?.misc;
      const cached = store?.get(cacheKey);
      if (cached && cached.dataRef === dataRef) return cached.data;
      const data = normalizeCaseMatrix(dataRef);
      store?.set(cacheKey, { dataRef, data });
      return data;
    },

    getNonEmptyOutputRows() {
      return this.getOutputMatrix().filter(rowHasMeaningfulData);
    },

    buildLightweightCaseSummary() {
      const previous = this.caseSummary || createEmptyCaseSummary();
      const mainData = this.getMatrixForLogicalSheet('main');
      const rawData = this.getMatrixForLogicalSheet('raw');
      const outputData = this.getOutputMatrix();
      const mainEntries = parseCaseEntries(mainData);
      const rawEntries = parseCaseEntries(rawData);
      const mainTransactionAnalysis = analyzeStartingMainTransactions(mainData);
      const mainEntryByStartRow = new Map(mainEntries.map(entry => [entry.startRow, entry]));
      const mainRowsForChecks = mainTransactionAnalysis.mainTransactionRows.map(item => item.row);
      const mainDateInfos = mainTransactionAnalysis.mainTransactionRows.map((item) => {
        const entry = mainEntryByStartRow.get(item.rowIndex) || { rows: [{ rowIndex: item.rowIndex, row: item.row }] };
        return analyzeEntryUtcDateCompletion(entry.rows);
      });
      const mainHashRows = mainData.slice(1).filter(row => normalizeCell(row?.[1]));
      const rawHashRows = rawData.slice(1).filter(row => normalizeCell(row?.[1]));
      const outputRows = outputData.filter(rowHasMeaningfulData).length;
      const destinationAddresses = new Set(mainData.slice(1).map(row => normalizeCell(row?.[3])).filter(Boolean));
      const attributionAnalysis = buildAddressAttributionAnalysis(mainData);
      const labelAnalysis = analyzeNumericMainPathLabels(mainData);

      return {
        ...previous,
        mainEntries: mainEntries.length,
        mainHashRows: mainHashRows.length,
        startingMainTransactions: mainTransactionAnalysis.mainTransactionCount,
        startingMainTrashRows: mainTransactionAnalysis.trashRowCount,
        startingMainDetectionMode: mainTransactionAnalysis.mode,
        rawEntries: rawEntries.length,
        rawHashRows: rawHashRows.length,
        outputRows,
        destinationAddressCount: destinationAddresses.size,
        caseAddressCount: attributionAnalysis.addressCount || 0,
        attributedAddressCount: attributionAnalysis.completeCount || 0,
        missingAttributionAddressCount: attributionAnalysis.missingCount || 0,
        attributionApplicableRowCount: attributionAnalysis.rowOccurrenceCount || 0,
        attributionFilledRowCount: Math.max(0, (attributionAnalysis.rowOccurrenceCount || 0) - (attributionAnalysis.missingRowCount || 0)),
        missingAttributionOccurrences: attributionAnalysis.missingOccurrenceCount || 0,
        missingDates: mainDateInfos.filter(info => !info.complete).length,
        invalidDates: mainDateInfos.filter(info => info.invalidCount > 0 || info.hasConflict).length,
        missingNetworks: mainRowsForChecks.filter(row => !normalizeCell(row?.[19]) || !normalizeCell(row?.[20])).length,
        missingPathLabels: labelAnalysis.missingCount + labelAnalysis.invalidCount + labelAnalysis.sequenceIssueCount + labelAnalysis.continuationLabelCount,
        missingAttribution: attributionAnalysis.missingOccurrenceCount || 0,
        refreshedAtUtc: new Date().toISOString()
      };
    },

    buildCaseSummary() {
      const mainData = this.getMatrixForLogicalSheet('main');
      const rawData = this.getMatrixForLogicalSheet('raw');
      const outputData = this.getOutputMatrix();
      const mainEntries = parseCaseEntries(mainData);
      const rawEntries = parseCaseEntries(rawData);
      const mainTransactionAnalysis = analyzeStartingMainTransactions(mainData);
      const mainEntryByStartRow = new Map(mainEntries.map(entry => [entry.startRow, entry]));
      const mainRowsForChecks = mainTransactionAnalysis.mainTransactionRows.map(item => item.row);
      const mainDateInfos = mainTransactionAnalysis.mainTransactionRows.map((item) => {
        const entry = mainEntryByStartRow.get(item.rowIndex) || { rows: [{ rowIndex: item.rowIndex, row: item.row }] };
        return analyzeEntryUtcDateCompletion(entry.rows);
      });
      const mainHashRows = mainData.slice(1).filter(row => normalizeCell(row?.[1]));
      const rawHashRows = rawData.slice(1).filter(row => normalizeCell(row?.[1]));
      const outputRows = outputData.filter(rowHasMeaningfulData).length;
      const destinationAddresses = new Set(mainData.slice(1).map(row => normalizeCell(row?.[3])).filter(Boolean));
      const rawCoverage = buildMainRawDataCoverage(mainData, rawData);
      const saplingAnalysis = buildSaplingPreparationAnalysis(mainData, rawData, this.caseSupportState.workflowState || {});
      const attributionAnalysis = buildAddressAttributionAnalysis(mainData);
      const pathOperationAnalysis = buildPathOperationAnalysis(mainData, rawData);
      const duplicateHashAnalysis = buildCaseDuplicateHashLabelerAnalysis(mainData, this.caseSupportState.workflowState || {}, { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' });
      const noteHashRewriteAnalysis = buildCaseNoteHashRewriteAnalysis(mainData, this.caseSupportState.workflowState || {}, { caseId: this.currentProjectFileId || this.caseDisplayId || 'case', includeUnstructured: this.noteHashRewriteIncludeUnstructured });
      const addressLegendAnalysis = buildCaseAddressLegendAnalysis(mainData, this.depositHotWalletEffectiveCurator, this.caseSupportState.workflowState || {}, { labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately });
      const duplicateHashCount = duplicateHashAnalysis.duplicateHashGroupCount || 0;
      const labelAnalysis = analyzeNumericMainPathLabels(mainData);
      return {
        mainEntries: mainEntries.length,
        mainHashRows: mainHashRows.length,
        startingMainTransactions: mainTransactionAnalysis.mainTransactionCount,
        startingMainTrashRows: mainTransactionAnalysis.trashRowCount,
        startingMainDetectionMode: mainTransactionAnalysis.mode,
        rawEntries: rawEntries.length,
        rawHashRows: rawHashRows.length,
        rawDataSets: rawCoverage.rawSets?.length || 0,
        rawMatchedMainTransactions: rawCoverage.coveredCount || 0,
        rawCoveredMainTransactions: rawCoverage.coveredCount || 0,
        rawMissingMainTransactions: rawCoverage.missingCount || 0,
        rawMismatchedMainTransactions: rawCoverage.mismatchCount || 0,
        saplingPreparedMainTransactions: saplingAnalysis.processedCount || 0,
        saplingUnprocessedMainTransactions: saplingAnalysis.unprocessedCount || 0,
        saplingReprocessAvailableTransactions: saplingAnalysis.reprocessAvailableCount || 0,
        qualifiedPathOutputs: pathOperationAnalysis.qualifiedCount || 0,
        pathEndingOutputs: pathOperationAnalysis.pathEndingCount || 0,
        graftReadyPathEndings: pathOperationAnalysis.graftReadyCount || 0,
        outputRows,
        destinationAddressCount: destinationAddresses.size,
        caseAddressCount: attributionAnalysis.addressCount || 0,
        attributedAddressCount: attributionAnalysis.completeCount || 0,
        missingAttributionAddressCount: attributionAnalysis.missingCount || 0,
        attributionApplicableRowCount: attributionAnalysis.rowOccurrenceCount || 0,
        attributionFilledRowCount: Math.max(0, (attributionAnalysis.rowOccurrenceCount || 0) - (attributionAnalysis.missingRowCount || 0)),
        missingAttributionOccurrences: attributionAnalysis.missingOccurrenceCount || 0,
        missingDates: mainDateInfos.filter(info => !info.complete).length,
        invalidDates: mainDateInfos.filter(info => info.invalidCount > 0 || info.hasConflict).length,
        missingNetworks: mainRowsForChecks.filter(row => !normalizeCell(row?.[19]) || !normalizeCell(row?.[20])).length,
        missingPathLabels: labelAnalysis.missingCount + labelAnalysis.invalidCount + labelAnalysis.sequenceIssueCount + labelAnalysis.continuationLabelCount,
        missingAttribution: attributionAnalysis.missingOccurrenceCount || 0,
        duplicateHashCount,
        duplicateHashGroupCount: duplicateHashAnalysis.duplicateHashGroupCount || 0,
        duplicateHashEntryCount: duplicateHashAnalysis.duplicateEntryCount || 0,
        duplicateHashEligibleUpdateCount: duplicateHashAnalysis.eligibleEntryCount || 0,
        duplicateHashBlockedProposalCount: duplicateHashAnalysis.blockedProposalCount || 0,
        staleDuplicateHashNoteCount: duplicateHashAnalysis.staleNoteCount || 0,
        noteHashRewriteEligibleCount: noteHashRewriteAnalysis.eligibleCount || 0,
        originalNotesBackupRows: noteHashRewriteAnalysis.backupRowCount || 0,
        addressLegendCandidateCount: addressLegendAnalysis.candidateCount || 0,
        addressLegendEntryCount: addressLegendAnalysis.legendEntryCount || 0,
        addressLegendChangedCount: addressLegendAnalysis.changedCount || 0,
        addressLegendReviewIsCurrent: !!addressLegendAnalysis.reviewIsCurrent,
        refreshedAtUtc: new Date().toISOString()
      };
    },

    refreshCaseSummary(options = {}) {
      this.caseSummary = {
        ...createEmptyCaseSummary(),
        ...(options.light ? this.buildLightweightCaseSummary() : this.buildCaseSummary())
      };
      return this.caseSummary;
    },

    buildCheckResult(id, status, message, details = {}) {
      return {
        id,
        status,
        message,
        details,
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        checkedAtUtc: new Date().toISOString()
      };
    },

    async runWorkflowChecks() {
      if (this.caseChecksRunning) {
        this.showCaseToast('Workflow checks are already running.');
        return Object.values(this.caseSupportState.checkResultsById || {});
      }

      const operationToken = this.beginCaseGlobalOperation({
        kind: 'workflow-checks',
        title: 'Running workflow checks',
        message: 'Preparing workflow check snapshot…',
        progress: 0,
        total: 8
      });

      try {
        await this.yieldToCaseUi();

        this.updateCaseGlobalOperation({
          progress: 1,
          message: 'Refreshing case summary…',
          detail: 'Reading main, raw, and output sheets.'
        }, operationToken);
        const summary = this.refreshCaseSummary();
        await this.yieldToCaseUi();

        this.updateCaseGlobalOperation({
          progress: 2,
          message: 'Checking core spreadsheet structure…',
          detail: 'Reviewing case tabs, starting transactions, dates, networks, and chronology.'
        }, operationToken);
        const mainSheet = this.findLogicalSheetMeta('main');
        const rawSheet = this.findLogicalSheetMeta('raw');
        const mainData = this.getMatrixForLogicalSheet('main');
        const mainSheetState = mainSheet?.id ? this.getSheetStateById(mainSheet.id) : createCaseSheetState({ includeHeaders: true });
        const mainTransactionAnalysis = analyzeStartingMainTransactions(mainData);
        const startingMainTransactionsReady = mainTransactionAnalysis.mainTransactionCount > 0 && mainTransactionAnalysis.trashRowCount === 0;
        const mainEntriesByStartRow = new Map(parseCaseEntries(mainData).map(entry => [entry.startRow, entry]));
        const mainDateInfos = mainTransactionAnalysis.mainTransactionRows.map((item) => {
          const entry = mainEntriesByStartRow.get(item.rowIndex) || { rows: [{ rowIndex: item.rowIndex, row: item.row }] };
          return analyzeEntryUtcDateCompletion(entry.rows);
        });
        const missingOrInvalidDateCount = mainDateInfos.filter(info => !info.complete).length;
        const parsedDates = mainDateInfos.filter(info => info.complete && info.canonicalDate).map(info => parseCaseUtcDate(info.canonicalDate).timestamp).filter(value => Number.isFinite(value));
        const chronologyPass = missingOrInvalidDateCount === 0 && parsedDates.every((value, index) => index === 0 || value >= parsedDates[index - 1]);
        const caseDetailsFilled = !!(this.caseDetails.caseName || this.caseDetails.caseId || this.caseDetails.caseType);
        await this.yieldToCaseUi();

        this.updateCaseGlobalOperation({
          progress: 3,
          message: 'Checking preparation and attribution state…',
          detail: 'Reviewing raw coverage, sapling preparation, attribution, and path operation candidates.'
        }, operationToken);
        const rawData = this.getMatrixForLogicalSheet('raw');
        const rawCoverage = buildMainRawDataCoverage(mainData, rawData);
        const saplingAnalysis = buildSaplingPreparationAnalysis(mainData, rawData, this.caseSupportState.workflowState || {});
        const attributionAnalysis = buildAddressAttributionAnalysis(mainData);
        const pathOperationAnalysis = buildPathOperationAnalysis(mainData, rawData);
        await this.yieldToCaseUi();

        this.updateCaseGlobalOperation({
          progress: 4,
          message: 'Checking review and cleanup stages…',
          detail: 'Reviewing colors, inconsistencies, deposit/hot-wallet notes, known-entity casing, duplicate hashes, note rewrites, and Column H USD references.'
        }, operationToken);
        const addressColorAnalysis = buildCaseAddressColorCoverageAnalysis(mainSheetState, this.caseSupportState.workflowState || {}, this.caseSupportState.caseDataRevision);
        const inconsistencyAnalysis = buildCaseInconsistencyReviewAnalysis(mainData, this.caseSupportState.workflowState.inconsistencyReview, this.caseInconsistencyRuleOptions);
        const depositHotWalletAnalysis = buildCaseDepositHotWalletReviewAnalysis(mainData, this.depositHotWalletEffectiveCurator, this.caseSupportState.workflowState || {}, this.depositHotWalletVendorResultsByKey);
        const depositPatternAnalysis = buildCaseDepositPatternReviewAnalysis(mainData, this.caseSupportState.workflowState || {}, { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' });
        const knownEntityCasingAnalysis = buildCaseKnownEntityCasingReviewAnalysis(mainData, this.depositHotWalletEffectiveCurator, this.caseSupportState.workflowState || {}, { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' });
        const duplicateHashAnalysis = buildCaseDuplicateHashLabelerAnalysis(mainData, this.caseSupportState.workflowState || {}, { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' });
        const noteHashRewriteAnalysis = buildCaseNoteHashRewriteAnalysis(mainData, this.caseSupportState.workflowState || {}, { caseId: this.currentProjectFileId || this.caseDisplayId || 'case', includeUnstructured: this.noteHashRewriteIncludeUnstructured });
        const wwwUsdReferenceState = buildCaseWwwUsdReferenceReviewState(mainData, this.caseSupportState.workflowState || {}, {
          caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
          caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0
        });
        const addressLegendAnalysis = buildCaseAddressLegendAnalysis(mainData, this.depositHotWalletEffectiveCurator, this.caseSupportState.workflowState || {}, { labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately });
        await this.yieldToCaseUi();

        this.updateCaseGlobalOperation({
          progress: 5,
          message: 'Checking deliverable builders…',
          detail: 'Reviewing suspicious/exchange/dormant tables, What Went Where, conclusion, and report body.'
        }, operationToken);
        const suspiciousAddressTableState = buildCaseSuspiciousAddressTableState(mainData, this.depositHotWalletEffectiveCurator, this.caseSupportState.workflowState || {}, {
          caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
          labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately,
          legendAnalysis: addressLegendAnalysis
        });
        const exchangeAddressTableState = buildCaseExchangeAddressTableState(mainData, this.depositHotWalletEffectiveCurator, this.caseSupportState.workflowState || {}, {
          caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
          labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately,
          legendAnalysis: addressLegendAnalysis
        });
        const dormantAddressDeliverableState = buildCaseDormantAddressDeliverablesState(mainData, this.depositHotWalletEffectiveCurator, this.caseSupportState.workflowState || {}, {
          caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
          labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately,
          legendAnalysis: addressLegendAnalysis
        });
        const whatWentWhereState = buildCaseWhatWentWhereState(mainData, this.depositHotWalletEffectiveCurator, this.caseSupportState.workflowState || {}, {
          caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
          caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0
        });
        const caseConclusionState = buildCaseConclusionState(whatWentWhereState, this.caseSupportState.workflowState || {}, {
          caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0
        });
        const reportBodyState = buildCaseReportBodyState(mainData, this.depositHotWalletEffectiveCurator, this.caseSupportState.workflowState || {}, {
          caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
          caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          legendAnalysis: addressLegendAnalysis
        });
        await this.yieldToCaseUi();

        this.updateCaseGlobalOperation({
          progress: 6,
          message: 'Checking addendum and final package state…',
          detail: 'Reviewing addendum path endings, recipient entities, in-app flow visuals, and one-click report package.'
        }, operationToken);
        const addendumPathEndingReviewState = this.getAddendumPathEndingReviewState({ whatWentWhereState });
        const addendumRecipientEntityState = this.getAddendumRecipientEntityReviewState({ pathEndingState: addendumPathEndingReviewState });
        const caseAddendumState = this.getCaseAddendumState({ recipientState: addendumRecipientEntityState, addressLegendAnalysis });
        const caseFlowVisualsState = this.getCaseFlowVisualsState();
        const fullReportPackageState = this.getCaseFullReportPackageState({
          includeDeliverableViews: false,
          addressLegendAnalysis,
          whatWentWhereState,
          caseConclusionState,
          reportBodyState,
          caseAddendumState,
          visualsState: caseFlowVisualsState
        });
        const labelAnalysis = analyzeNumericMainPathLabels(mainData);
        const pathLabelExamples = labelAnalysis.examples.length > 0
          ? ` Examples: ${labelAnalysis.examples.map(example => `${abbreviateCaseBuddyHash(example.hash)} row ${example.row}, expected ${example.expected}${example.current ? `, current ${example.current}` : ''}`).join(' | ')}`
          : '';
        await this.yieldToCaseUi();

        this.updateCaseGlobalOperation({
          progress: 7,
          message: 'Assembling check results…',
          detail: 'Writing the latest pass/fail/warn snapshot into workflow state.'
        }, operationToken);
        const results = [
          this.buildCheckResult(
            'case_sheets',
            mainSheet && rawSheet ? 'pass' : 'fail',
            mainSheet && rawSheet ? 'Main Data and Raw Data tabs are present.' : 'Main Data and Raw Data tabs need to be created.'
          ),
          this.buildCheckResult(
            'main_transactions',
            mainTransactionAnalysis.mainTransactionCount > 0 && mainTransactionAnalysis.trashRowCount === 0 ? 'pass' : 'fail',
            mainTransactionAnalysis.mainTransactionCount > 0
              ? mainTransactionAnalysis.trashRowCount === 0
                ? `${mainTransactionAnalysis.mainTransactionCount} starting main transaction${mainTransactionAnalysis.mainTransactionCount === 1 ? '' : 's'} detected (${mainTransactionAnalysis.mode}).`
                : `${mainTransactionAnalysis.mainTransactionCount} starting main transaction${mainTransactionAnalysis.mainTransactionCount === 1 ? '' : 's'} detected, but ${mainTransactionAnalysis.trashRowCount} row${mainTransactionAnalysis.trashRowCount === 1 ? '' : 's'} have Column D without Column B in the starting-data area.`
              : 'No starting main transactions detected. Add rows with transaction hashes in Column B, with optional To Addresses in Column D.'
          ),
          this.buildCheckResult(
            'networks',
            !startingMainTransactionsReady ? 'warn' : summary.missingNetworks === 0 ? 'pass' : 'fail',
            !startingMainTransactionsReady ? 'Confirm starting main transactions before checking networks.' : summary.missingNetworks === 0 ? 'Starting/main transaction rows have Chain Id and Chain Name. Subtransaction rows are not checked at this stage.' : `${summary.missingNetworks} starting/main transaction row(s) are missing Chain Id or Chain Name. Subtransaction rows are not checked at this stage.`
          ),
          this.buildCheckResult(
            'dates',
            !startingMainTransactionsReady ? 'warn' : summary.missingDates === 0 ? 'pass' : 'fail',
            !startingMainTransactionsReady ? 'Confirm starting main transactions before checking dates.' : summary.missingDates === 0 ? 'Starting/main transaction rows have valid Column E UTC dates. Subtransaction rows are not checked at this stage.' : `${summary.missingDates} starting/main transaction row(s) are missing or have invalid Column E UTC dates. Subtransaction rows are not checked at this stage.`
          ),
          this.buildCheckResult(
            'chronology',
            !startingMainTransactionsReady || summary.missingDates > 0 ? 'warn' : chronologyPass ? 'pass' : 'fail',
            !startingMainTransactionsReady ? 'No confirmed starting main transactions to order yet.' : summary.missingDates > 0 ? 'Fill valid UTC dates for starting/main transactions before chronology can be confirmed.' : chronologyPass ? 'Starting/main transaction blocks appear chronologically ordered. Subtransaction rows travel with their parent block.' : 'Starting/main transaction blocks are not chronologically ordered by Column E.'
          ),
          this.buildCheckResult(
            'path_labels',
            labelAnalysis.total === 0 ? 'warn' : labelAnalysis.pass ? 'pass' : 'fail',
            labelAnalysis.total === 0
              ? 'No eligible top-level main transaction entries were found for path-label checking.'
              : labelAnalysis.pass
                ? `${labelAnalysis.total} top-level main transaction entr${labelAnalysis.total === 1 ? 'y has' : 'ies have'} standard numeric Column A labels in UTC chronological order.`
                : `${labelAnalysis.completeCount}/${labelAnalysis.total} top-level main transaction entries have standard numeric Column A labels. Missing: ${labelAnalysis.missingCount}; invalid: ${labelAnalysis.invalidCount}; sequence issues: ${labelAnalysis.sequenceIssueCount}; missing UTC dates: ${labelAnalysis.missingDateCount}; continuation label cells: ${labelAnalysis.continuationLabelCount}.${pathLabelExamples}`,
            {
              total: labelAnalysis.total,
              complete: labelAnalysis.completeCount,
              missing: labelAnalysis.missingCount,
              invalid: labelAnalysis.invalidCount,
              sequenceIssues: labelAnalysis.sequenceIssueCount,
              missingDates: labelAnalysis.missingDateCount,
              continuationLabelCells: labelAnalysis.continuationLabelCount
            }
          ),
          this.buildCheckResult(
            'case_details',
            caseDetailsFilled ? 'pass' : 'warn',
            caseDetailsFilled ? 'At least one case detail is filled.' : 'Case name, case id, and case type are optional but not currently filled.'
          ),
          this.buildRawDataCoverageCheckResult(rawCoverage),
          this.buildTool68PreparationCheckResult(saplingAnalysis),
          this.buildAddressAttributionCheckResult(attributionAnalysis),
          this.buildCheckResult(
            'path_decisions',
            pathOperationAnalysis.qualifiedCount > 0 ? 'warn' : 'warn',
            pathOperationAnalysis.qualifiedCount > 0
              ? `${pathOperationAnalysis.qualifiedCount} qualified output row${pathOperationAnalysis.qualifiedCount === 1 ? '' : 's'} are available for prune/graft/Bridge review; ${pathOperationAnalysis.pathEndingCount} current path ending${pathOperationAnalysis.pathEndingCount === 1 ? '' : 's'} can stage graft or Bridge work. Use the path operation modal or mark the step manually when review is complete.`
              : 'No qualified output rows with funds followed and left bounds were detected yet. This is expected before sapling preparation or after a very small zero-hop case.'
          ),
          this.buildCheckResult(
            'address_colors',
            addressColorAnalysis.addressCellCount === 0 ? 'warn' : addressColorAnalysis.complete ? 'pass' : 'fail',
            addressColorAnalysis.addressCellCount === 0
              ? 'No From/To address cells were detected in Main Data Column C/D yet.'
              : addressColorAnalysis.complete
                ? `Address coloring is current for revision ${this.caseSupportState.caseDataRevision}. ${addressColorAnalysis.coloredAddressCellCount} C/D address cell${addressColorAnalysis.coloredAddressCellCount === 1 ? '' : 's'} are highlighted across ${addressColorAnalysis.uniqueAddressCount} unique address${addressColorAnalysis.uniqueAddressCount === 1 ? '' : 'es'}.`
                : addressColorAnalysis.run
                  ? 'Main Data has changed or address colors were cleared since the last C/D address coloring run. Re-run Color C/D Addresses or mark the workflow step manually.'
                  : 'C/D address coloring has not been run for this case yet.',
            addressColorAnalysis
          ),
          this.buildEntityReviewCheckResult(),
          this.buildCheckResult('external_review', 'warn', 'External attribution review is an investigator acknowledgement. Use the workflow button or manual completion after reviewing addresses in outside tools.'),
          this.buildCaseInconsistencyReviewCheckResult(inconsistencyAnalysis),
          this.buildDepositHotWalletReviewCheckResult(depositHotWalletAnalysis),
          this.buildDepositPatternReviewCheckResult(depositPatternAnalysis),
          this.buildKnownEntityCasingReviewCheckResult(knownEntityCasingAnalysis),
          this.buildDuplicateHashLabelerCheckResult(duplicateHashAnalysis),
          this.buildNoteHashRewriteCheckResult(noteHashRewriteAnalysis),
          this.buildWwwUsdReferenceReviewCheckResult(wwwUsdReferenceState),
          this.buildAddressLegendReviewCheckResult(addressLegendAnalysis),
          this.buildTemplateDeliverableCheckResult(),
          this.buildSuspiciousAddressTableCheckResult(suspiciousAddressTableState),
          this.buildExchangeAddressTableCheckResult(exchangeAddressTableState),
          this.buildDormantAddressDeliverablesCheckResult(dormantAddressDeliverableState),
          this.buildWhatWentWhereCheckResult(whatWentWhereState),
          this.buildCaseConclusionCheckResult(caseConclusionState),
          this.buildReportBodyCheckResult(reportBodyState),
          this.buildAddendumPathEndingReviewCheckResult(addendumPathEndingReviewState),
          this.buildAddendumRecipientEntityCheckResult(addendumRecipientEntityState),
          this.buildCaseAddendumCheckResult(caseAddendumState),
          this.buildCaseFlowVisualsCheckResult(caseFlowVisualsState),
          this.buildCaseFullReportPackageCheckResult(fullReportPackageState),
          this.buildCheckResult(
            'deliverables_ready',
            summary.mainEntries > 0 && labelAnalysis.pass ? 'pass' : 'warn',
            summary.mainEntries > 0 && labelAnalysis.pass ? 'Basic deliverable prerequisites are present.' : 'Deliverables need main entries and standard numeric path labels before most outputs can be current.'
          )
        ];
        await this.yieldToCaseUi();

        this.updateCaseGlobalOperation({
          progress: 8,
          message: 'Saving check snapshot…',
          detail: 'Updating workflow status cards without changing spreadsheet rows.'
        }, operationToken);
        this.caseSupportState.checkResultsById = results.reduce((accumulator, result) => {
          accumulator[result.id] = result;
          return accumulator;
        }, {});
        this.caseSupportState.lastValidationRevision = Number(this.caseSupportState.caseDataRevision) || 0;
        this.caseSupportState.lastValidationUtc = new Date().toISOString();
        this.persistCaseSupportState();
        this.finishCaseGlobalOperation(operationToken, { message: 'Workflow checks refreshed.' });
        this.showCaseToast('Workflow checks refreshed.');
        return results;
      } catch (error) {
        console.warn('Case Buddy workflow checks failed.', error);
        const message = error?.message ? `Workflow checks failed: ${error.message}` : 'Workflow checks failed.';
        this.finishCaseGlobalOperation(operationToken, {
          message: 'Workflow checks stopped.',
          detail: error?.message || 'Review the browser console for details.',
          hideDelayMs: 1200
        });
        this.showCaseToast(message);
        throw error;
      }
    },

    async runWorkflowChecksFromStaleBadge(step = null) {
      if (this.caseChecksRunning) {
        this.showCaseToast('Workflow checks are already running.');
        return Object.values(this.caseSupportState.checkResultsById || {});
      }
      const title = step?.title || 'this stale workflow step';
      this.showCaseToast(`Running checks to refresh ${title}.`);
      return this.runWorkflowChecks();
    },

    openWorkflowStepHelp(step) {
      const tag = step?.tag || '';
      if (!tag) return;
      this.activeWorkflowStepHelpTag = tag;
      this.showWorkflowStepHelpModal = true;
    },

    closeWorkflowStepHelpModal() {
      this.showWorkflowStepHelpModal = false;
      this.activeWorkflowStepHelpTag = '';
    },

    getWorkflowStepHelpView(stepOrTag) {
      const tag = typeof stepOrTag === 'string' ? stepOrTag : (stepOrTag?.tag || '');
      const groups = Array.isArray(CASE_WORKFLOW_GROUPS) ? CASE_WORKFLOW_GROUPS : [];
      const definition = groups.flatMap(group => group.steps || []).find(step => step.tag === tag) || null;
      const help = getCaseWorkflowStepHelp(tag);
      if (!definition && !help) return null;
      return {
        tag,
        title: help?.title || definition?.title || 'Workflow step',
        summary: help?.summary || definition?.description || '',
        goal: help?.goal || 'Complete this workflow step using the available tool buttons.',
        why: help?.why || 'This step supports the case workflow without replacing the spreadsheet as the source of truth.',
        whatToDo: help?.whatToDo?.length ? help.whatToDo : ['Use the primary action button for this step, then review any output before marking it complete.'],
        doneWhen: help?.doneWhen?.length ? help.doneWhen : ['The step is complete when the related check, signal, or review state becomes current.'],
        tips: help?.tips || [],
        stepTitle: definition?.title || help?.title || 'Workflow step',
        stepDescription: definition?.description || help?.summary || '',
        actionLabels: (definition?.actions || []).map(action => action.label).filter(Boolean)
      };
    },

    getWorkflowStepSignalCode(step) {
      return step.signalCode || step.actions?.find(action => action.signalCode)?.signalCode || '';
    },

    buildWorkflowStepView(step) {
      const revision = Number(this.caseSupportState.caseDataRevision) || 0;
      const manual = !!this.caseSupportState.workflowState.manualCompleteByTag?.[step.tag]?.completedAtUtc;
      const signalCode = this.getWorkflowStepSignalCode(step);
      const signalRecord = signalCode ? this.caseSupportState.workflowState.signalCompleteByCode?.[signalCode] : null;
      const signal = !!(signalRecord?.completedAtUtc && (!step.signalRequiresCurrentRevision || Number(signalRecord.revision) === revision));
      const results = (step.checkIds || []).map(id => this.caseSupportState.checkResultsById?.[id]).filter(Boolean);
      const checksRequested = (step.checkIds || []).length > 0;
      const checksRun = checksRequested && results.length === step.checkIds.length;
      const stale = results.some(result => Number(result.revision) !== revision);
      const check = checksRun && !stale && results.every(result => result.status === 'pass');
      const failed = checksRun && results.some(result => result.status === 'fail');
      const warned = checksRun && results.some(result => result.status === 'warn');
      const complete = manual || signal || check;
      let status = 'ready';
      let statusLabel = 'Ready';

      if (complete) {
        status = 'complete';
        statusLabel = 'Complete';
      } else if (stale) {
        status = 'stale';
        statusLabel = 'Stale';
      } else if (failed) {
        status = 'attention';
        statusLabel = 'Needs fix';
      } else if (warned) {
        status = 'attention';
        statusLabel = 'Needs review';
      } else if (!checksRun && checksRequested) {
        status = 'unchecked';
        statusLabel = 'Unchecked';
      }

      return {
        ...step,
        displayOrder: String(step.order).padStart(2, '0'),
        complete,
        stale,
        sources: { manual, signal, check },
        status,
        statusLabel,
        statusClass: `workflow-step-card--${status}`,
        actions: (step.actions || []).map(action => ({
          ...action,
          disabled: !!action.disabled ||
            (action.kind === 'checks' && this.caseChecksRunning) ||
            (action.kind === 'network-discovery' && this.networkLookupRunning) ||
            (action.kind === 'date-lookup' && this.dateLookupRunning) ||
            (action.kind === 'date-sort' && this.dateLookupRunning) ||
            (action.kind === 'main-path-label-assignment' && this.mainPathLabelAssignRunning) ||
            (action.kind === 'address-attribution-fill' && this.addressAttributionRunning) ||
            (action.kind === 'path-operation' && this.pathOperationRunning) ||
            (action.kind === 'inconsistency-review' && this.caseInconsistencyRunning) ||
            (action.kind === 'deposit-hot-wallet-normalization' && this.depositHotWalletRunning) ||
            (action.kind === 'known-entity-note-casing-review' && this.knownEntityCasingRunning) ||
            (action.kind === 'duplicate-hash-labeler' && this.duplicateHashLabelerRunning) ||
            (action.kind === 'note-hash-label-rewrite' && this.noteHashRewriteRunning) ||
            (action.kind === 'www-usd-reference-review' && this.whatWentWhereEstimateRunning) ||
            (action.kind === 'address-legend-review' && this.addressLegendRunning) ||
            (action.kind === 'address-legend-deliverable' && this.addressLegendRunning) ||
            (action.kind === 'suspicious-address-table-review' && this.addressLegendRunning) ||
            (action.kind === 'exchange-address-table-review' && this.addressLegendRunning) ||
            (action.kind === 'dormant-address-deliverable-review' && this.addressLegendRunning) ||
            (action.kind === 'what-went-where-review' && this.whatWentWhereEstimateRunning) ||
            (action.kind === 'report-body-review' && this.addressLegendRunning) ||
            (action.kind === 'addendum-path-ending-review' && this.addendumPathEndingRefreshRunning) ||
            (action.kind === 'addendum-recipient-review' && this.addendumRecipientRefreshRunning) ||
            (action.kind === 'addendum-build-review' && this.caseAddendumRefreshRunning) ||
            (action.kind === 'address-color-clear' && this.pathOperationRunning)
        }))
      };
    },

    toggleManualWorkflowComplete(step) {
      const tag = step?.tag;
      if (!tag) return;

      const manualState = this.caseSupportState.workflowState.manualCompleteByTag || {};
      if (manualState[tag]) {
        delete manualState[tag];
        this.showCaseToast('Manual completion removed.');
      } else {
        manualState[tag] = {
          completedAtUtc: new Date().toISOString(),
          revision: Number(this.caseSupportState.caseDataRevision) || 0
        };
        this.showCaseToast('Manual completion recorded.');
      }
      this.caseSupportState.workflowState.manualCompleteByTag = { ...manualState };
      this.persistCaseSupportState();
    },

    recordWorkflowSignal(signalCode, source = '', options = {}) {
      if (!signalCode) return;
      this.caseSupportState.workflowState.signalCompleteByCode = {
        ...(this.caseSupportState.workflowState.signalCompleteByCode || {}),
        [signalCode]: {
          completedAtUtc: new Date().toISOString(),
          revision: Number(this.caseSupportState.caseDataRevision) || 0,
          source
        }
      };
      this.persistCaseSupportState();
      if (!options.silent) {
        this.showCaseToast(`Signal recorded: ${signalCode}.`);
      }
    },

    isWorkflowGroupOpen(group) {
      const groupId = group?.id || '';
      if (!groupId) return false;
      const foldouts = this.caseSupportState.uiState?.foldouts?.workflowGroups || {};
      if (Object.prototype.hasOwnProperty.call(foldouts, groupId)) {
        return !!foldouts[groupId];
      }
      return !!group.defaultOpen;
    },

    onWorkflowGroupToggle(group, event) {
      const groupId = group?.id || '';
      if (!groupId || !event?.target) return;
      const current = this.caseSupportState.uiState || createDefaultCaseUiState();
      this.caseSupportState.uiState = normalizeCaseUiState({
        ...current,
        foldouts: {
          ...(current.foldouts || {}),
          workflowGroups: {
            ...(current.foldouts?.workflowGroups || {}),
            [groupId]: !!event.target.open
          }
        }
      });
      this.persistCaseSupportState();
    },

    isDeliverableGroupOpen(group) {
      const groupId = group?.id || '';
      if (!groupId) return false;
      const foldouts = this.caseSupportState.uiState?.foldouts?.deliverableGroups || {};
      if (Object.prototype.hasOwnProperty.call(foldouts, groupId)) {
        return !!foldouts[groupId];
      }
      return !!group.defaultOpen;
    },

    onDeliverableGroupToggle(group, event) {
      const groupId = group?.id || '';
      if (!groupId || !event?.target) return;
      const current = this.caseSupportState.uiState || createDefaultCaseUiState();
      this.caseSupportState.uiState = normalizeCaseUiState({
        ...current,
        foldouts: {
          ...(current.foldouts || {}),
          deliverableGroups: {
            ...(current.foldouts?.deliverableGroups || {}),
            [groupId]: !!event.target.open
          }
        }
      });
      this.persistCaseSupportState();
    },

    runWorkflowAction(action, step) {
      if (!action) return;

      if (action.kind === 'starting-main-transactions') {
        this.openStartingMainTransactionsModal();
        return;
      }

      if (action.kind === 'starting-main-confirm') {
        this.confirmStartingMainTransactionsLoaded();
        return;
      }

      if (action.kind === 'network-discovery') {
        this.openNetworkLookupModal();
        return;
      }

      if (action.kind === 'date-lookup') {
        this.openDateLookupModal();
        return;
      }

      if (action.kind === 'date-sort') {
        this.sortMainEntriesByDate();
        return;
      }

      if (action.kind === 'main-path-label-assignment') {
        this.openMainPathLabelModal();
        return;
      }

      if (action.kind === 'raw-data-import') {
        this.openRawDataImportModal();
        return;
      }

      if (action.kind === 'wayfinder-handoff') {
        this.openWayFinderHandoffModal();
        return;
      }

      if (action.kind === 'raw-data-request-placeholder') {
        this.stageRawDataRequestPlaceholder(action, step);
        return;
      }

      if (action.kind === 'tool68-preparation') {
        this.openTool68PreparationModal();
        return;
      }

      if (action.kind === 'address-attribution-fill') {
        this.openAddressAttributionModal();
        return;
      }

      if (action.kind === 'path-operation') {
        this.openPathOperationModal();
        return;
      }

      if (action.kind === 'entity-curator-review') {
        this.openEntityCuratorReviewModal();
        return;
      }

      if (action.kind === 'inconsistency-review') {
        this.openCaseInconsistencyReviewModal();
        return;
      }

      if (action.kind === 'deposit-hot-wallet-normalization') {
        this.openDepositHotWalletModal();
        return;
      }

      if (action.kind === 'deposit-pattern-review') {
        this.openDepositPatternReviewModal();
        return;
      }

      if (action.kind === 'known-entity-note-casing-review') {
        this.openKnownEntityCasingReviewModal();
        return;
      }

      if (action.kind === 'duplicate-hash-labeler') {
        this.openDuplicateHashLabelerModal();
        return;
      }

      if (action.kind === 'duplicate-hash-deliverable') {
        this.buildDuplicateHashTablePreview();
        return;
      }

      if (action.kind === 'note-hash-label-rewrite') {
        this.openNoteHashRewriteModal();
        return;
      }

      if (action.kind === 'original-notes-backup-deliverable') {
        this.stageOriginalNotesBackupPreview();
        return;
      }

      if (action.kind === 'www-usd-reference-review') {
        this.openWwwUsdReferenceReviewModal();
        return;
      }

      if (action.kind === 'www-usd-reference-audit-stage') {
        this.stageWwwUsdReferenceReviewAudit();
        return;
      }

      if (action.kind === 'address-legend-review') {
        this.openAddressLegendModal();
        return;
      }

      if (action.kind === 'address-legend-deliverable') {
        this.stageAddressLegendPreview();
        return;
      }

      if (action.kind === 'template-deliverable-review') {
        this.openTemplateDeliverableModal();
        return;
      }

      if (action.kind === 'template-deliverable-stage') {
        this.stageTemplateDeliverablePreview();
        return;
      }

      if (action.kind === 'template-deliverable-download') {
        this.downloadTemplateDeliverableTsv();
        return;
      }

      if (action.kind === 'suspicious-address-table-review') {
        this.openSuspiciousAddressTableModal();
        return;
      }

      if (action.kind === 'suspicious-address-table-stage') {
        this.stageSuspiciousAddressTablePreview();
        return;
      }

      if (action.kind === 'suspicious-address-table-download') {
        this.downloadSuspiciousAddressTableTsv();
        return;
      }

      if (action.kind === 'exchange-address-table-review') {
        this.openExchangeAddressTableModal();
        return;
      }

      if (action.kind === 'exchange-address-table-stage') {
        this.stageExchangeAddressTablePreview();
        return;
      }

      if (action.kind === 'exchange-address-table-download') {
        this.downloadExchangeAddressTableTsv();
        return;
      }

      if (action.kind === 'dormant-address-deliverable-review') {
        this.openDormantAddressDeliverablesModal();
        return;
      }

      if (action.kind === 'dormant-address-table-stage') {
        this.stageDormantAddressTablePreview();
        return;
      }

      if (action.kind === 'dormant-address-monitoring-stage') {
        this.stageDormantAddressMonitoringPreview();
        return;
      }

      if (action.kind === 'dormant-address-table-download') {
        this.downloadDormantAddressTableTsv();
        return;
      }

      if (action.kind === 'dormant-address-monitoring-download') {
        this.downloadDormantAddressMonitoringTsv();
        return;
      }

      if (action.kind === 'what-went-where-review') {
        this.openWhatWentWhereModal();
        return;
      }

      if (action.kind === 'what-went-where-stage') {
        this.stageWhatWentWherePreview();
        return;
      }

      if (action.kind === 'what-went-where-audit-stage') {
        this.stageWhatWentWhereValuationAudit();
        return;
      }

      if (action.kind === 'what-went-where-download') {
        this.downloadWhatWentWhereTsv();
        return;
      }

      if (action.kind === 'case-conclusion-review') {
        this.openCaseConclusionModal();
        return;
      }

      if (action.kind === 'case-conclusion-stage') {
        this.stageCaseConclusionPreview();
        return;
      }

      if (action.kind === 'case-conclusion-download') {
        this.downloadCaseConclusionText();
        return;
      }

      if (action.kind === 'report-body-review') {
        this.openReportBodyModal();
        return;
      }

      if (action.kind === 'report-body-stage') {
        this.stageReportBodyPreview({ variantId: CASE_REPORT_BODY_VARIANT_STANDARD });
        return;
      }

      if (action.kind === 'report-body-custom-stage') {
        this.stageReportBodyPreview({ variantId: CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING });
        return;
      }

      if (action.kind === 'report-body-download') {
        this.downloadReportBodyText({ variantId: CASE_REPORT_BODY_VARIANT_STANDARD });
        return;
      }

      if (action.kind === 'report-body-copy-rich') {
        this.copyReportBodyRich({ variantId: CASE_REPORT_BODY_VARIANT_STANDARD });
        return;
      }

      if (action.kind === 'addendum-path-ending-review') {
        this.openAddendumPathEndingReviewModal();
        return;
      }

      if (action.kind === 'addendum-path-ending-stage') {
        this.stageAddendumPathEndingReviewRows();
        return;
      }

      if (action.kind === 'addendum-path-ending-audit') {
        this.stageAddendumPathEndingReviewAudit();
        return;
      }

      if (action.kind === 'addendum-recipient-review') {
        this.openAddendumRecipientEntityModal();
        return;
      }

      if (action.kind === 'addendum-recipient-stage') {
        this.stageAddendumRecipientEntityRows();
        return;
      }

      if (action.kind === 'addendum-recipient-audit') {
        this.stageAddendumRecipientEntityAudit();
        return;
      }

      if (action.kind === 'addendum-build-review') {
        this.openCaseAddendumModal();
        return;
      }

      if (action.kind === 'addendum-stage') {
        this.stageCaseAddendumPreview({ variantId: CASE_ADDENDUM_VARIANT_STRUCTURED });
        return;
      }

      if (action.kind === 'addendum-narrative-stage') {
        this.stageCaseAddendumPreview({ variantId: CASE_ADDENDUM_VARIANT_NARRATIVE });
        return;
      }

      if (action.kind === 'addendum-audit-stage') {
        this.stageCaseAddendumAudit();
        return;
      }

      if (action.kind === 'addendum-copy-rich') {
        this.copyCaseAddendumRich({ variantId: CASE_ADDENDUM_VARIANT_STRUCTURED });
        return;
      }

      if (action.kind === 'addendum-download') {
        this.downloadCaseAddendumText({ variantId: CASE_ADDENDUM_VARIANT_STRUCTURED });
        return;
      }

      if (action.kind === 'case-flow-visuals-review') {
        this.openCaseFlowVisualsModal();
        return;
      }

      if (action.kind === 'case-flow-visuals-stage-summary') {
        this.stageCaseFlowVisualsPlaceholder();
        return;
      }

      if (action.kind === 'case-flow-visuals-done') {
        this.markCaseFlowVisualsDone({ source: action.id || 'case_flow_visuals_done' });
        return;
      }

      if (action.kind === 'full-report-package-review') {
        this.openCaseFullReportPackageModal();
        return;
      }

      if (action.kind === 'full-report-package-copy-rich') {
        this.copyCaseFullReportPackageRich();
        return;
      }

      if (action.kind === 'full-report-package-download-html') {
        this.downloadCaseFullReportPackageHtml();
        return;
      }

      if (action.kind === 'address-color-like' || action.kind === 'address-coloring') {
        this.applyCaseAddressColors();
        return;
      }

      if (action.kind === 'address-color-clear') {
        this.clearCaseAddressColors();
        return;
      }

      if (action.kind === 'sheet') {
        this.activateLogicalSheet(action.target);
        return;
      }

      if (action.kind === 'section') {
        this.scrollToSection(action.target || 'case-output');
        return;
      }

      if (action.kind === 'checks') {
        this.runWorkflowChecks();
        return;
      }

      if (action.kind === 'scaffold') {
        this.ensureCaseWorkbookScaffold();
        this.seedCaseHeaders({ force: false });
        return;
      }

      if (action.kind === 'case-details') {
        this.openCaseDetailsEditor();
        return;
      }

      if (action.kind === 'signal') {
        this.recordWorkflowSignal(action.signalCode || this.getWorkflowStepSignalCode(step), action.id);
        return;
      }

      if (action.kind === 'deliverable') {
        const deliverable = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(item => item.id === action.target);
        if (deliverable) this.buildDeliverable(this.buildDeliverableView(deliverable));
        return;
      }

      this.stageToolSlotOutput(action, step);
    }
};
